// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
  type ShowModalOptions,
} from '@shared/ui-kit/components/popups/show-modal';
import {
  type BaseReporting,
  type ClientData,
} from '@shared/data-access/interfaces/api/ai/reporting';
import { reportingApi } from '@shared/data-access/api/ai/reporting-api';
import { type ThunkDispatch } from '@reduxjs/toolkit';
import { downloadFile } from '@shared/data-access/utils';
import type { TFunction } from 'i18next';

interface DownloadReports extends BaseReporting {
  readonly assetType: string;
}

export const downloadReports = (
  {
    assetType,
    portfolio_investment,
    portfolio_analysis,
    esg_analysis,
    portfolio_backtest,
  }: DownloadReports,
  dispatch: ThunkDispatch<any, any, any>,
  t: TFunction,
) => {
  showModal({
    ...MODAL_TEMPLATES_OPTIONS(t).DOWNLOAD_REPORTS,
    preConfirm: async () => {
      const clientName = document.getElementById('client-name') as HTMLInputElement;
      const clientAddress = document.getElementById(
        'client-address',
      ) as HTMLInputElement;
      const clientPhone = document.getElementById(
        'client-phone',
      ) as HTMLInputElement;
      const reportName = document.getElementById('report-name') as HTMLInputElement;

      const clientData: ClientData = {
        client_name: clientName.value,
        client_address: clientAddress.value,
        client_phone: clientPhone.value,
        report_name: reportName.value,
      };

      const baseReportingPayload = {
        portfolio_analysis,
        portfolio_investment,
        client_data: clientData,
      };

      const reportingPayload =
        assetType === 'stock'
          ? { ...baseReportingPayload, esg_analysis }
          : { ...baseReportingPayload, portfolio_backtest };

      if (!reportName.value) {
        showModal({
          ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
          title: t('research.unnamed-report-warning', { ns: 'research' }),
          content: t('research.unnamed-report-message', { ns: 'research' }),
        } as ShowModalOptions);
        return false;
      } else {
        try {
          const result = await dispatch(
            reportingApi.endpoints.portfolioAnalysis.initiate({
              payload: reportingPayload,
              assetType,
            }),
          ).unwrap();

          downloadFile(result);

          return true;
        } catch (error) {
          return error;
        }
      }
    },
  } as ShowModalOptions).then(result => {
    if (result.isConfirmed && typeof result.value !== 'boolean') {
      showModal({
        ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
        title: t('research.report-error', { ns: 'research' }),
        content: t('research.report-error-message', { ns: 'research' }),
        buttons: {
          confirm: { show: true, text: t('common.ok', '', { ns: 'common' }) },
        },
      } as ShowModalOptions);
    } else if (result.isConfirmed) {
      showModal({
        content: t('research.report-success', { ns: 'research' }),
        buttons: {
          confirm: { show: true, text: t('common.ok', '', { ns: 'common' }) },
        },
      } as ShowModalOptions);
    }
  });
};
